import { useEffect, useState } from "react";
import { apiFetchPublic } from "../lib/api";
import { photoUrl } from "../lib/assets";
import { LATEST_PHOTO_PLACEHOLDERS } from "../data/homePage";

/** @typedef {{ slot: number, src?: string, caption: string, background?: string, shadow?: string, focusX?: number, focusY?: number }} LatestPhotoDisplay */

/**
 * @param {{ slot: number, imageKey?: string | null, url?: string | null, caption?: string, focusX?: number, focusY?: number }} photo
 * @param {typeof LATEST_PHOTO_PLACEHOLDERS[number] | undefined} fallback
 * @returns {LatestPhotoDisplay}
 */
function photoToDisplay(photo, fallback) {
  if (photo.imageKey) {
    return {
      slot: photo.slot,
      src: photoUrl(photo),
      caption: photo.caption || "",
      focusX: typeof photo.focusX === "number" ? photo.focusX : 50,
      focusY: typeof photo.focusY === "number" ? photo.focusY : 50,
    };
  }

  return {
    slot: photo.slot,
    caption: fallback?.caption || "",
    background: fallback?.background,
    shadow: fallback?.shadow,
  };
}

/** @returns {{ photos: LatestPhotoDisplay[], loading: boolean }} */
export function useHomeLatestPhotos() {
  const [photos, setPhotos] = useState(/** @type {LatestPhotoDisplay[] | null} */ (null));

  useEffect(() => {
    let cancelled = false;

    async function loadPhotos() {
      try {
        const body = await apiFetchPublic("/home-latest-photos");
        const apiPhotos = body.data?.photos || [];

        if (!cancelled) {
          const display = Array.from({ length: 5 }, (_, index) => {
            const slot = index + 1;
            const photo =
              apiPhotos.find((item) => Number(item.slot) === slot) || { slot };
            const fallback = LATEST_PHOTO_PLACEHOLDERS[index];
            return photoToDisplay(photo, fallback);
          });
          setPhotos(display);
        }
      } catch {
        if (!cancelled) {
          setPhotos(
            LATEST_PHOTO_PLACEHOLDERS.map((fallback, index) => ({
              slot: index + 1,
              caption: fallback.caption,
              background: fallback.background,
              shadow: fallback.shadow,
            }))
          );
        }
      }
    }

    void loadPhotos();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    photos: photos ?? [],
    loading: photos === null,
  };
}
